import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Trash2, Phone, Mail, Calendar, Tag, Search, X } from "lucide-react";
import { Input } from "../components/ui/input";

const STATUSES = {
  new: { label: "Nou", color: "bg-[#00E5FF]" },
  contacted: { label: "Contactat", color: "bg-amber-500" },
  scheduled: { label: "Programat", color: "bg-blue-500" },
  done: { label: "Rezolvat", color: "bg-green-500" },
  rejected: { label: "Anulat", color: "bg-neutral-500" },
};

export default function AdminReservations() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    const r = await api.get("/admin/reservations");
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/reservations/${id}`, { status });
    toast.success("Status actualizat");
    load();
  };

  const remove = async (id) => {
    if (!confirm("Sterge rezervarea?")) return;
    await api.delete(`/admin/reservations/${id}`);
    load();
  };

  const filtered = items.filter((r) => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      r.name.toLowerCase().includes(q) ||
      r.phone.toLowerCase().includes(q) ||
      (r.email || "").toLowerCase().includes(q) ||
      r.scooter_model.toLowerCase().includes(q) ||
      (r.issue_description || "").toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="admin-reservations-page">
      <h1 className="font-display text-4xl uppercase">Rezervari online</h1>
      <p className="text-sm text-neutral-400 mt-1 mb-6">Cereri si intrebari de la clienti</p>

      <div className="mb-5 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <Input
            data-testid="reservation-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cauta dupa nume, telefon, model..."
            className="bg-neutral-900 border-neutral-800 rounded-none pl-10 pr-10 h-11 focus:border-[#00E5FF]"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-[#00E5FF]">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="reservation-status-filter" className="w-44 bg-neutral-900 border-neutral-800 rounded-none h-11">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-neutral-950 border-neutral-800 text-white">
            <SelectItem value="all">Toate statusurile</SelectItem>
            <SelectItem value="new">Noi</SelectItem>
            <SelectItem value="contacted">Contactate</SelectItem>
            <SelectItem value="scheduled">Programate</SelectItem>
            <SelectItem value="done">Rezolvate</SelectItem>
            <SelectItem value="rejected">Anulate</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs text-neutral-500 uppercase tracking-wider">
          {filtered.length} / {items.length}
        </div>
      </div>

      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="bg-neutral-950 border border-neutral-800 p-12 text-center text-neutral-500">
            {items.length === 0 ? "Nicio rezervare inca" : "Niciun rezultat"}
          </div>
        )}
        {filtered.map((r) => {
          const s = STATUSES[r.status] || STATUSES.new;
          return (
            <div key={r.id} data-testid={`reservation-row-${r.id}`} className="bg-neutral-950 border border-neutral-800 hover:border-[#00E5FF] transition-colors p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-widest text-white px-2 py-1 ${s.color}`}>{s.label}</span>
                    <span className="text-xs uppercase tracking-widest text-[#00E5FF] flex items-center gap-1"><Tag className="w-3 h-3" /> {r.type}</span>
                    <span className="font-display text-lg uppercase">{r.scooter_model}</span>
                  </div>
                  <div className="text-sm text-neutral-300 font-medium">{r.name}</div>
                  <div className="flex flex-wrap gap-4 mt-1 text-xs text-neutral-400">
                    <span className="flex items-center gap-1"><Phone className="w-3 h-3 text-[#00E5FF]" /> {r.phone}</span>
                    {r.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3 text-[#00E5FF]" /> {r.email}</span>}
                    {r.preferred_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3 text-[#00E5FF]" /> {r.preferred_date}</span>}
                  </div>
                  <p className="text-sm text-neutral-300 mt-3 bg-neutral-900 border border-neutral-800 p-3">{r.issue_description}</p>
                  <div className="text-xs text-neutral-500 mt-2">Trimis: {new Date(r.created_at).toLocaleString("ro-RO")}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                    <SelectTrigger data-testid={`reservation-status-${r.id}`} className="w-36 bg-neutral-900 border-neutral-800 rounded-none text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-neutral-950 border-neutral-800 text-white">
                      <SelectItem value="new">Nou</SelectItem>
                      <SelectItem value="contacted">Contactat</SelectItem>
                      <SelectItem value="scheduled">Programat</SelectItem>
                      <SelectItem value="done">Rezolvat</SelectItem>
                      <SelectItem value="rejected">Anulat</SelectItem>
                    </SelectContent>
                  </Select>
                  <button data-testid={`delete-reservation-${r.id}`} onClick={() => remove(r.id)} className="text-neutral-500 hover:text-red-500 p-2">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
